/* app/CommandPaletteContext.jsx — провайдер состояния командной палитры (S13, §2.1).
   Держит open/close/toggle + глобальный keydown-listener Ctrl/⌘K (везде, кроме фокуса
   в элементе с [data-no-palette] — напр. textarea Composer).

   Индекс поиска строится лениво при первом открытии: грузим БОЕВЫЕ реестры через lib/api
   (Сырьё/ТТК+п-ф/Меню/скиллы) ОДИН раз и кэшируем, плюс текущий список чатов из
   ChatProvider (включая дочерние/spawned). При смене набора чатов индекс пересобирается
   из кэша сущностей (без повторных сетевых запросов).
   ХЕНДОФФ → frontend/src/contexts/CommandPaletteContext.jsx. */

import React from 'react';
import { useAuth } from './providers/AuthProvider.jsx';
import { useChat } from './providers/ChatProvider.jsx';
import { listIngredients, listMenuBriefs, listSkills, listTTKBriefs } from '../lib/api/index.js';
import { searchIndex } from '../lib/searchIndex.js';

  const { createContext, useContext, useState, useEffect, useCallback, useRef } = React;

  const Ctx = createContext(null);

  function CommandPaletteProvider({ children }) {
    const [open, setOpen] = useState(false);
    const chat = useChat ? useChat() : null;
    // активное пространство: индекс строится под его тарифные флаги (закрытая «Аналитика»
    // из палитры пропадает); смена/обновление пространства пересобирает индекс
    const auth = useAuth ? useAuth() : null;
    const activeWorkspace = auth ? auth.activeWorkspace : null;

    const openPalette = useCallback(() => setOpen(true), []);
    const closePalette = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((v) => !v), []);

    // глобальный Ctrl/⌘K
    useEffect(() => {
      const onKey = (e) => {
        const isK = (e.key === 'k' || e.key === 'K' || e.key === 'л' || e.key === 'Л');
        if (!isK || !(e.metaKey || e.ctrlKey)) return;
        // не перехватываем, если фокус в защищённом поле (Composer и т.п.)
        const ae = document.activeElement;
        if (ae && ae.closest && ae.closest('[data-no-palette]')) return;
        e.preventDefault();
        setOpen((v) => !v);
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, []);

    // боевые сущности (Сырьё/ТТК/Меню/скиллы) — грузим ОДИН раз при первом открытии.
    // Каждый запрос best-effort: сбой одного реестра не рушит палитру (даёт []).
    const [entities, setEntities] = useState(null);
    const loadingRef = useRef(false);
    useEffect(() => {
      if (!open || entities || loadingRef.current) return;
      loadingRef.current = true;
      Promise.all([
        listIngredients().catch(() => []),
        listTTKBriefs().catch(() => []),
        listMenuBriefs().catch(() => []),
        listSkills().catch(() => []),
      ]).then(([ingredients, recipes, menus, skills]) => {
        setEntities({ ingredients, recipes, menus, skills });
      }).finally(() => { loadingRef.current = false; });
    }, [open, entities]);

    // строим индекс при открытии; пересобираем при загрузке сущностей или смене набора чатов
    const [index, setIndex] = useState(null);
    const chats = chat ? chat.chats : null;
    useEffect(() => {
      if (!open) return;
      setIndex(searchIndex.buildSearchIndex({
        ingredients: (entities && entities.ingredients) || [],
        recipes: (entities && entities.recipes) || [],
        menus: (entities && entities.menus) || [],
        chats: chats || [],
        skills: (entities && entities.skills) || [],
        workspace: activeWorkspace,
      }));
    }, [open, entities, chats, activeWorkspace]);

    const value = {
      open, openPalette, closePalette, toggle,
      index: index || [],
      // последние чаты для пустого состояния
      recentChats: (chats || []).slice(0, 5),
    };
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  export const useCommandPalette = () => useContext(Ctx) || { open: false, openPalette: () => {}, closePalette: () => {}, toggle: () => {}, index: [], recentChats: [] };

export { CommandPaletteProvider };
